import { attendanceShape } from '~/packages/publicHealth/attendance/stats/attendanceStats.definition'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prefixProperties from '~/prix/utils/prefixProperties'
import attendanceOccurrenceStatsAction from './attendanceOccurrenceStats.action.json' with { type: 'json' }
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { dateOrIsoString } from '~/prix/types/date'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import {
  defaultDetectionLayers,
} from '~/utils/server/stats/defaultTimeSeriesLayers'

const shape = z.object({
  icd10Code: z.string().describe('Código CID-10').nullish(),
  icpc2Code: z.string().describe('Código CIAP-2').nullish(),
  ...prefixProperties('attendance.', attendanceShape.shape),
})

const expressionForCharacteristics = expression(shape.shape)
const expressionForAssertions = expression(shape.shape)

export const attendanceOccurrenceVariable: Variable = {
  key: 'attendanceOccurrence',
  actionDefinitionKey: attendanceOccurrenceStatsAction.key,
  title: 'Ocorrência em saúde',
  categoryKey: 'healthAttendanceOccurrences',
  unitSingular: 'ocorrência',
  unitPlural: 'ocorrências',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: getZodSchemaInfo(shape),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.attendanceOccurrence,
  basicSelect: ['attendance.citizen.name', 'attendance.attendanceDateTime'],
  detailedSelect: ['attendance.citizen.sex', 'attendance.citizen.birthDate'],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(expressionForAssertions),
}

const attendanceOccurrenceStatsDefinition = defineAction({
  ...attendanceOccurrenceStatsAction,
  input: z.object({
    variable,
    highlight: expressionForCharacteristics.optional(),
    filter: expressionForCharacteristics.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(shape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default attendanceOccurrenceStatsDefinition
